"use client";

import { useEffect, useState } from "react";
import {
  getRecentRequestIds,
  subscribeRequestIds,
  type RequestIdEntry,
} from "@/lib/request-id-buffer";

/**
 * Floating bottom-right pill that surfaces the most-recent client-observed
 * X-Request-ID. Click to copy the ID; hover/expand to see the last 25.
 *
 * Only mounts when NEXT_PUBLIC_DEBUG_REQUEST_IDS=1 — the env flag is
 * evaluated at module load so an unset flag short-circuits before render.
 * Independent of role: if the flag is on, the badge is visible. Production
 * users see nothing because the flag defaults off.
 *
 * The badge subscribes to request-id-buffer's change stream and re-renders
 * on every new entry. The subscription is bounded by the buffer's own
 * try/catch — listener errors cannot stall fetches.
 */
export function RequestIdBadge() {
  const [entries, setEntries] = useState<RequestIdEntry[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [copiedRid, setCopiedRid] = useState<string | null>(null);

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_DEBUG_REQUEST_IDS !== "1") return;
    setEntries(getRecentRequestIds());
    return subscribeRequestIds(() => setEntries(getRecentRequestIds()));
  }, []);

  if (process.env.NEXT_PUBLIC_DEBUG_REQUEST_IDS !== "1") return null;

  const latest = entries[0];

  async function handleCopy(rid: string) {
    try {
      await navigator.clipboard.writeText(rid);
      setCopiedRid(rid);
      setTimeout(() => setCopiedRid((c) => (c === rid ? null : c)), 1500);
    } catch {
      // Clipboard may not be available (insecure contexts); silent.
    }
  }

  return (
    <div className="pointer-events-none fixed bottom-3 right-3 z-[9999] flex flex-col items-end gap-1">
      {expanded && entries.length > 0 && (
        <div className="pointer-events-auto max-h-72 w-80 overflow-y-auto rounded-md border bg-background/95 p-2 text-[11px] shadow-lg backdrop-blur">
          <div className="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
            <span>Recent request IDs</span>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              className="rounded px-1 hover:bg-muted"
              aria-label="Collapse"
            >
              ×
            </button>
          </div>
          <ul className="space-y-1">
            {entries.map((e) => (
              <li key={`${e.rid}-${e.ts}`} className="flex items-baseline gap-2">
                <button
                  type="button"
                  onClick={() => handleCopy(e.rid)}
                  className="font-mono text-[10px] text-foreground hover:underline"
                  title="Copy ID"
                >
                  {e.rid.slice(0, 8)}
                </button>
                <span
                  className={`text-[10px] ${
                    e.status >= 500
                      ? "text-destructive"
                      : e.status >= 400
                        ? "text-amber-600"
                        : "text-muted-foreground"
                  }`}
                >
                  {e.status}
                </span>
                <span className="truncate text-[10px] text-muted-foreground">
                  {e.url}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {latest && (
        <button
          type="button"
          onClick={() => {
            if (expanded) {
              handleCopy(latest.rid);
            } else {
              setExpanded(true);
            }
          }}
          className="pointer-events-auto rounded-full border bg-background/95 px-3 py-1 font-mono text-[10px] text-muted-foreground shadow-md backdrop-blur hover:text-foreground"
          title={
            expanded
              ? "Click to copy the most recent request ID"
              : "Click to view recent request IDs"
          }
        >
          {copiedRid === latest.rid ? "copied!" : `rid ${latest.rid.slice(0, 8)}`}
        </button>
      )}
    </div>
  );
}
